import {
  slab, wallX, wallZ, goal, sign, neon, checkpoint, edgeLights, hazard,
} from '../kit.js';

/**
 * A three hundred metre well, descended in four launches.
 *
 * The terraces zigzag between two faces of the canyon: each one throws you the
 * opposite way to the last. That is not decoration. If every stage launched
 * the same direction, the next stage's launch wall would sit square in the
 * arrival path and you would eat it at fifty metres a second.
 *
 * The measurements are load bearing:
 *  - the portal band starts six metres up so a launch clears its own terrace
 *  - the pit's far wall stops twenty four metres short so the launch clears it
 *  - the far terrace's launch wall runs eight metres past the top of the
 *    portal band, so an over-aimed arrival is caught instead of sailing past
 *
 * @param {number} dir -1 launches toward -Z, +1 toward +Z.
 */
function terrace(x, y, z, dir, { depth = 62, w = 26, d = 30 } = {}) {
  const halfD = d / 2;
  const backZ = z - dir * (halfD + 0.7);   // the wall you launch out of
  const out = [
    slab(x, y, z, w, d, 'plate', 1.4),
    ...edgeLights(x, y, z, w, d, 'cyan'),
    wallX(x - w / 2 - 0.7, y, z, d, 7, 'plate', 1.4),
    wallX(x + w / 2 + 0.7, y, z, d, 7, 'plate', 1.4),

    wallZ(x, y, backZ, w + 2, 6, 'plate', 1.4),          // dead band: too low to launch from
    wallZ(x, y + 6, backZ, w + 2, 14, 'panel', 1.4),     // the portal band
    wallZ(x, y + 20, backZ, w + 2, 8, 'plate', 1.4),     // backstop for high arrivals
    neon(x, y + 6.2, backZ + dir * 0.75, [w, 0.16, 0.16], 'cyan'),
    neon(x, y + 19.8, backZ + dir * 0.75, [w, 0.16, 0.16], 'cyan'),
  ];

  // The pit sits on the launch side, its lip flush with the terrace edge.
  const pz = z + dir * (halfD + 12);
  out.push(
    wallZ(x, y - depth, pz - dir * 12.75, 28, depth, 'plate', 1.5),
    wallZ(x, y - depth, pz + dir * 12.75, 28, depth - 24, 'plate', 1.5),
    wallX(x - 12.75, y - depth, pz, 24, depth, 'plate', 1.5),
    wallX(x + 12.75, y - depth, pz, 24, depth, 'plate', 1.5),
    slab(x, y - depth, pz, 26, 24, 'panel', 1.5),
    neon(x, y - depth + 0.6, pz, [24, 0.16, 0.16], 'magenta'),
  );
  return out;
}

export default {
  id: 'descent',
  name: 'THE DESCENT',
  blurb: 'Three hundred metres down, one launch at a time.',
  mode: { type: 'sprint', par: 75 },
  redline: { seconds: 68 },   // docs/REDLINE.md; seeded from the board, honesty pass pending
  spawn: { p: [0, 240.4, 8], yaw: 0 },
  killY: -300,
  env: {
    fog: 0x081226,
    fogDensity: 0.0026,
    sky: { zenith: 0x02040d, horizon: 0x0d1a3a, glow: 0x2b3f9a, aurora: 0x2fd0ff },
    floorY: -460,
  },
  objects: [
    // ---- stage 1: north face, launches away from you
    ...terrace(0, 240, 0, -1),
    sign(0, 244.4, -6, 0, [
      'THE DESCENT',
      'PORTAL THE PIT FLOOR',
      'PORTAL THE WALL BEHIND YOU, UP HIGH',
    ]),
    // x -8.5: at -11 the 8.4m line ran 1.5m into the west wall (inner face -13).
    sign(-8.5, 243.2, 6, 0, ['EVERY STAGE THROWS YOU BACK'], '#ffb020'),

    // ---- stage 2: far face, throws you home again
    ...terrace(0, 176, -121, 1),
    checkpoint(0, 176, -128),

    // ---- stage 3
    ...terrace(0, 112, 0, -1),
    checkpoint(0, 112, 7),
    hazard(0, 112.3, -4, 18, 5),

    // ---- stage 4
    ...terrace(0, 48, -121, 1),
    checkpoint(0, 48, -128),

    // ---- surface level
    slab(0, -16, 15, 90, 150, 'plate', 2.5),
    ...edgeLights(0, -16, 15, 90, 150, 'lime'),
    wallZ(0, -16, 91, 92, 34, 'plate', 2.5),
    wallX(-46, -16, 15, 150, 18, 'plate', 2.5),
    wallX(46, -16, 15, 150, 18, 'plate', 2.5),
    neon(0, 6, 89.5, [86, 0.22, 0.22], 'lime'),
    checkpoint(0, -16, 18),
    goal(0, -16, 45, 180),
    sign(0, -10, 41, 0, ['SURFACE LEVEL'], '#9dff3c'),
  ],
};
